import { useEffect, useRef, useState } from "react";
import { Github, KeyRound, Loader2, CheckCircle2, Circle, Download, Upload, Wand2, FileCode, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const btn = "h-9 px-3 rounded-lg border border-gray-800 text-xs text-gray-300 hover:text-amber-400 hover:border-amber-500/40 inline-flex items-center gap-1.5 transition-colors disabled:opacity-60";

const Step = ({ done, children, testid }) => (
  <li data-testid={testid} className={"flex items-center gap-2 text-xs " + (done ? "text-emerald-400" : "text-gray-400")}>
    {done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />} {children}
  </li>
);

function UploadKey({ onDone }) {
  const file = useRef(null);
  const [f, setF] = useState({ store_password: "", key_alias: "", key_password: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const picked = file.current?.files?.[0];
    if (!picked) return toast.error("Choose your keystore file (.jks, .keystore or .p12)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", picked);
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      onDone((await api.post("/channel-builder/settings/keystore/upload", fd)).data);
      toast.success("Signing key saved and sent to GitHub");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't use that keystore"); } finally { setBusy(false); }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-3 rounded-lg border border-gray-800 p-3" data-testid="keystore-upload-form">
      <input ref={file} type="file" accept=".jks,.keystore,.p12,.pfx" data-testid="keystore-file" className="sm:col-span-2 text-xs text-gray-300 file:mr-3 file:rounded-md file:border-0 file:bg-gray-800 file:px-3 file:py-1.5 file:text-gray-200" />
      {[["store_password", "Keystore password", "password"], ["key_alias", "Key alias", "text"], ["key_password", "Key password (if different)", "password"]].map(([k, l, t]) => (
        <div key={k} className="space-y-1">
          <Label className="text-gray-400 text-[11px]">{l}</Label>
          <Input data-testid={`keystore-${k}`} type={t} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="bg-gray-950 border-gray-800 h-9 text-sm" />
        </div>
      ))}
      <div className="flex items-end"><button data-testid="keystore-upload-submit" onClick={send} disabled={busy} className={btn}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Use this key</button></div>
    </div>
  );
}

export function ChannelSettings({ onReady }) {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [repo, setRepo] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [showYml, setShowYml] = useState(false);

  const apply = (d) => { setS(d); setRepo(d.repo); onReady?.(d.ready); };
  useEffect(() => { api.get("/channel-builder/settings").then((r) => { apply(r.data); setOpen(!r.data.ready); }).catch(() => {}); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const act = async (key, fn, msg) => {
    setBusy(key);
    try { apply((await fn()).data); if (msg) toast.success(msg); setToken(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); } finally { setBusy(""); }
  };
  if (!s) return null;
  const spin = (k, Icon) => (busy === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />);

  return (
    <div className="rounded-2xl border border-gray-800 bg-card" data-testid="channel-settings">
      <button data-testid="channel-settings-toggle" onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 px-5 py-4 text-left">
        <Github className="h-5 w-5 text-amber-400" />
        <div className="flex-1">
          <p className="font-display font-semibold text-white">Automatic app builds (GitHub)</p>
          <p data-testid="channel-settings-status" className={"text-xs " + (s.ready ? "text-emerald-400" : "text-gray-500")}>
            {s.ready ? `Ready · signed APK and app bundle built in ${s.repo}` : "Not set up yet · builds include the Android project only"}
          </p>
        </div>
        <ChevronDown className={"h-4 w-4 text-gray-500 transition-transform " + (open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="px-5 pb-5 grid lg:grid-cols-[1fr_240px] gap-6 border-t border-gray-800 pt-5">
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-sm font-semibold text-white">1. GitHub repository</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-gray-400 text-[11px]">Repository (owner/repo)</Label>
                  <Input data-testid="gh-repo-input" value={repo} onChange={(e) => setRepo(e.target.value)} placeholder="yourname/church-tv-builds" className="bg-gray-950 border-gray-800 font-mono text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-400 text-[11px]">Fine-grained token {s.has_token && <span className="text-gray-500">(saved {s.token_hint}, leave blank to keep)</span>}</Label>
                  <Input data-testid="gh-token-input" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="github_pat_…" className="bg-gray-950 border-gray-800 font-mono text-sm" />
                </div>
              </div>
              <p className="text-[11px] text-gray-500 leading-relaxed">Create it at GitHub → Settings → Developer settings → Fine-grained tokens. Choose only this repository and give <span className="text-gray-300">Actions, Contents, Secrets and Workflows: Read and write</span>.</p>
              <button data-testid="gh-save" onClick={() => act("gh", () => api.put("/channel-builder/settings", { repo, token: token || null }), "Connected to GitHub")} disabled={!!busy} className={btn}>{spin("gh", Github)} Save & connect</button>
            </div>
            <div className="space-y-3">
              <p className="text-sm font-semibold text-white">2. Build recipe</p>
              <p className="text-xs text-gray-400">Adds <span className="font-mono text-gray-300">.github/workflows/streamworship-android.yml</span> to {s.repo || "the repository"}{s.default_branch ? ` (${s.default_branch} branch)` : ""}.</p>
              <div className="flex flex-wrap gap-2">
                <button data-testid="gh-install-workflow" onClick={() => act("wf", () => api.post("/channel-builder/settings/workflow"), "Build recipe added to GitHub")} disabled={!!busy || !s.has_token} className={btn}>{spin("wf", FileCode)} {s.workflow_installed ? "Update build recipe" : "Add build recipe"}</button>
                <button data-testid="gh-show-workflow" onClick={() => setShowYml(!showYml)} className="text-xs text-gray-400 hover:text-white">{showYml ? "Hide file" : "Show file"}</button>
              </div>
              {showYml && <pre data-testid="gh-workflow-yml" className="max-h-64 overflow-auto rounded-lg bg-gray-950 border border-gray-800 p-3 text-[11px] text-gray-300">{s.workflow_yml}</pre>}
            </div>
            <div className="space-y-3">
              <p className="text-sm font-semibold text-white">3. Signing key</p>
              {s.keystore ? (
                <div data-testid="keystore-info" className="rounded-lg bg-gray-950 border border-gray-800 p-3 text-xs text-gray-400 space-y-1">
                  <p><span className="text-gray-300">{s.keystore.source === "generated" ? "Created by the portal" : "Your uploaded key"}</span> · {s.keystore.type.toUpperCase()} · alias <span className="font-mono text-gray-300">{s.keystore.alias}</span>{s.keystore.expires && ` · valid until ${s.keystore.expires}`}</p>
                  {s.keystore.fingerprint && <p className="font-mono text-[10px] break-all">SHA-256 {s.keystore.fingerprint}</p>}
                </div>
              ) : <p className="text-xs text-gray-400">No key yet. Create one, or upload the key you already use for your apps.</p>}
              <div className="flex flex-wrap gap-2">
                <button data-testid="keystore-generate" disabled={!!busy} className={btn}
                  onClick={() => (!s.keystore || window.confirm("Replace the current signing key? Apps already published with the old key can't be updated with a new one.")) && act("gen", () => api.post("/channel-builder/settings/keystore/generate"), "New signing key created")}>
                  {spin("gen", Wand2)} {s.keystore ? "Create a new key" : "Create a key for me"}
                </button>
                <button data-testid="keystore-upload-toggle" onClick={() => setShowUpload(!showUpload)} className={btn}><KeyRound className="h-3.5 w-3.5" /> Upload my key</button>
                {s.keystore && <a data-testid="keystore-backup" href={`${API}/channel-builder/settings/keystore/backup`} className={btn}><Download className="h-3.5 w-3.5" /> Download backup</a>}
              </div>
              {s.keystore?.source === "generated" && <p className="text-[11px] text-amber-300/90">Download the backup and keep it somewhere safe. Store updates must be signed with this same key.</p>}
              {showUpload && <UploadKey onDone={(d) => { apply(d); setShowUpload(false); }} />}
            </div>
          </div>
          <ul className="space-y-2.5 lg:border-l lg:border-gray-800 lg:pl-6" data-testid="gh-checklist">
            <Step done={s.has_token && !!s.repo} testid="gh-step-connected">Connected to GitHub</Step>
            <Step done={s.workflow_installed} testid="gh-step-workflow">Build recipe added</Step>
            <Step done={!!s.keystore} testid="gh-step-key">Signing key ready</Step>
            <Step done={!!s.secrets_synced_at} testid="gh-step-secrets">Key sent to GitHub securely</Step>
          </ul>
        </div>
      )}
    </div>
  );
}
